'use client';

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { IconX } from '@tabler/icons-react';
import Button from './Button';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';
type ConfirmVariant = 'primary' | 'danger';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    description?: string;
    size?: ModalSize;
    closeOnBackdrop?: boolean;
    children?: ReactNode;
    footer?: ReactNode;
}

interface ConfirmDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    variant?: ConfirmVariant;
    loading?: boolean;
}

const sizeMap: Record<ModalSize, string> = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
};

/**
 * Modal dialog dengan palette Notion (Decision 8).
 */
export default function Modal({
    open,
    onClose,
    title,
    description,
    size = 'md',
    closeOnBackdrop = true,
    children,
    footer,
}: ModalProps) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        // Tablet/HP (<1280px atau layar sentuh): modal DIRATAKAN KE ATAS dan tingginya dibatasi 55dvh — tanpa mendeteksi
        // keyboard — supaya saat keyboard layar muncul bagian modal yang sedang diisi tetap di atas keyboard.
        // Desktop: di tengah layar, maks 90dvh.
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 max-xl:items-start max-xl:pt-3 pointer-coarse:items-start pointer-coarse:pt-3">
            <div
                className="absolute inset-0 bg-slate-900/50"
                onClick={() => closeOnBackdrop && onClose?.()}
                aria-hidden="true"
            />
            <div
                role="dialog"
                aria-modal="true"
                className={clsx(
                    'relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-h-[90dvh] max-xl:max-h-[55dvh] pointer-coarse:max-h-[55dvh] flex flex-col',
                    sizeMap[size],
                )}
            >
                {(title || description) && (
                    <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-3 border-b border-slate-200">
                        <div className="flex-1 min-w-0">
                            {title && <h2 className="text-base font-semibold text-slate-800">{title}</h2>}
                            {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="p-1 pointer-coarse:p-3 -m-1 text-slate-500 hover:bg-slate-100 rounded-md transition-colors"
                        >
                            <IconX size={18} />
                        </button>
                    </div>
                )}
                <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
                {footer && (
                    <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Convenience confirm dialog dengan 2 button (cancel / ok).
 */
export function ConfirmDialog({
    open,
    onClose,
    onConfirm,
    title = 'Konfirmasi',
    message = 'Anda yakin?',
    confirmText = 'OK',
    cancelText = 'Batal',
    variant = 'primary',
    loading = false,
}: ConfirmDialogProps) {
    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title}
            size="sm"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={loading}>
                        {cancelText}
                    </Button>
                    <Button variant={variant} onClick={onConfirm} loading={loading}>
                        {confirmText}
                    </Button>
                </>
            }
        >
            <p className="text-sm text-slate-700">{message}</p>
        </Modal>
    );
}
